import { useEffect, useState } from 'react';

export interface SectionRef {
  id: string;
  label: string;
}

/** Pure: choose the active section id given each section's top offset. */
export function pickActiveSection(
  sections: SectionRef[],
  topOf: (id: string) => number,
  offset: number,
): string {
  let active = sections[0]?.id ?? '';
  for (const s of sections) {
    if (topOf(s.id) <= offset) active = s.id;
  }
  return active;
}

interface Props {
  sections: SectionRef[];
  activeId?: string;
}

export default function ContentsIndex({
  sections,
  activeId: controlled,
}: Props) {
  const [active, setActive] = useState(controlled ?? sections[0]?.id ?? '');

  useEffect(() => {
    if (controlled !== undefined) {
      setActive(controlled);
      return;
    }
    const onScroll = () => {
      const next = pickActiveSection(
        sections,
        (id) =>
          document.getElementById(id)?.getBoundingClientRect().top ?? Infinity,
        120,
      );
      setActive(next);
    };
    onScroll();
    // Capture phase on document so we catch scrolling inside nested containers
    // (the page scrolls inside .CONTENT__COLUMN, not the window).
    document.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    });
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true });
  }, [sections, controlled]);

  return (
    <nav className="POST__ANALYSIS__index" aria-label="Sections">
      <p className="POST__ANALYSIS__index__title">Contents</p>
      <ul>
        {sections.map((s, i) => (
          <li key={s.id} className={active === s.id ? 'is-active' : ''}>
            <a
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById(s.id)
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <span className="POST__ANALYSIS__index__num">
                {String(i + 1).padStart(2, '0')}
              </span>
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
